function loadGalleryAssets() {
  try {
    // IMPORTANT: Adjust the path based on where you place this file
    // If this file is in src/utils/, use '../assets/prodgallery'
    // If this file is in src/webpopup/, use '../assets/prodgallery'
    const context = require.context(
      '../assets/prodgallery',
      true,
      /\.(png|jpe?g|webp|mp4|webm)$/i
    );
    
    const assets = {};
    
    context.keys().forEach((key) => {
      const url = context(key);
      assets[key] = url;
    });
    
    return assets;
  } catch (error) {
    console.warn('⚠️ Failed to load gallery assets:', error);
    console.warn('Make sure the path in require.context matches your project structure');
    return {};
  }
}

const galleryModules = loadGalleryAssets();

/**
 * Parse file path to extract category, product name, and file info
 * Example: ./agriculture/truuna/truuna.png
 * Returns: { category: 'agriculture', productName: 'truuna', fileName: 'truuna.png', ext: 'png' }
 */
function parseGalleryPath(path) {
  // Remove leading './' from require.context paths
  const cleanPath = path.replace(/^\.\//, '');
  const parts = cleanPath.split('/');
  
  // Handle different path depths
  if (parts.length < 2) {
    console.warn('Invalid gallery path structure:', path);
    return null;
  }
  
  const fileName = parts[parts.length - 1];
  const productName = parts[parts.length - 2];
  const category = parts.length >= 3 ? parts[parts.length - 3] : parts[0];
  const ext = fileName.split('.').pop().toLowerCase();
  
  return {
    category,
    productName,
    fileName,
    ext,
    isVideo: ['mp4', 'webm'].includes(ext),
    isImage: ['png', 'jpg', 'jpeg', 'webp'].includes(ext)
  };
}

/**
 * Organize gallery assets into a nested structure
 * Returns nested structure for easy lookup
 */
function organizeGalleryAssets() {
  const organized = {};
  
  Object.entries(galleryModules).forEach(([path, url]) => {
    const parsed = parseGalleryPath(path);
    if (!parsed) return;
    
    const { category, productName, fileName, isVideo, isImage } = parsed;
    
    // Skip if structure is invalid
    if (!category || !productName || !fileName) {
      console.warn('Skipping invalid path:', path);
      return;
    }
    
    // Initialize nested structure
    if (!organized[category]) {
      organized[category] = {};
    }
    // Initialize product entry if it doesn't exist
    // This ensures we have a consistent structure even if some products only have images or videos
    if (!organized[category][productName]) {
      organized[category][productName] = {
        images: [],
        videos: [],
        all: []
      };
    }
    
    // Handle different Webpack module formats
    const assetUrl = typeof url === 'string' ? url : (url.default || url);
    // add asset data to the appropriate category/product
    const assetData = {
      path,
      url: assetUrl,
      fileName,
      type: isVideo ? 'video' : 'image'
    };
    
    // Categorize by type
    if (isVideo) {
      organized[category][productName].videos.push(assetData);
    } else if (isImage) {
      organized[category][productName].images.push(assetData);
    }
    
    organized[category][productName].all.push(assetData);
  });
  
  // Sort assets by filename for consistent ordering
  Object.values(organized).forEach(categoryData => {
    Object.values(categoryData).forEach(productData => {
      productData.images.sort((a, b) => a.fileName.localeCompare(b.fileName));
      productData.videos.sort((a, b) => a.fileName.localeCompare(b.fileName));
      productData.all.sort((a, b) => a.fileName.localeCompare(b.fileName));
    });
  });
  
  return organized;
}

// Create the organized gallery structure
const galleryAssets = organizeGalleryAssets();

/**
 * Get all assets (images + videos) for a specific product
 * 
 * @param {string} category - Category name (e.g., 'agriculture', 'hospitility')
 * @param {string} productName - Product name (e.g., 'truuna', 'serving')
 * @returns {Array} Array of asset objects with { url, fileName, type }
 */
export function getProductGallery(category, productName) {
  // Normalize names (lowercase, remove spaces)
  const normalizedCategory = String(category || '').toLowerCase().trim();
  const normalizedProduct = String(productName || '').toLowerCase().trim();
  
  const categoryData = galleryAssets[normalizedCategory];
  if (!categoryData) {
    console.warn(`⚠️ Category "${category}" not found in gallery assets`);
    console.log('Available categories:', Object.keys(galleryAssets));
    return [];
  }
  
  const productData = categoryData[normalizedProduct];
  if (!productData) {
    console.warn(`⚠️ Product "${productName}" not found in category "${category}"`);
    console.log('Available products:', Object.keys(categoryData));
    return [];
  }
  
  return productData.all;
}

/**
 * Get only images for a specific product
 */
export function getProductImages(category, productName) {
  const normalizedCategory = String(category || '').toLowerCase().trim();
  const normalizedProduct = String(productName || '').toLowerCase().trim();
  
  const categoryData = galleryAssets[normalizedCategory];
  if (!categoryData) return [];
  
  const productData = categoryData[normalizedProduct];
  if (!productData) return [];
  
  return productData.images;
}

/**
 * Get only videos for a specific product
 */
export function getProductVideos(category, productName) {
  const normalizedCategory = String(category || '').toLowerCase().trim();
  const normalizedProduct = String(productName || '').toLowerCase().trim();
  
  const categoryData = galleryAssets[normalizedCategory];
  if (!categoryData) return [];
  
  const productData = categoryData[normalizedProduct];
  if (!productData) return [];
  
  return productData.videos;
}

/**
 * Debug: Get all available categories
 */
export function getAvailableCategories() {
  return Object.keys(galleryAssets);
}

/**
 * Debug: Get all products in a category
 */
export function getProductsInCategory(category) {
  const normalizedCategory = String(category || '').toLowerCase().trim();
  const categoryData = galleryAssets[normalizedCategory];
  return categoryData ? Object.keys(categoryData) : [];
}

/**
 * Debug: Log entire gallery structure (useful for debugging)
 */
export function debugGalleryStructure() {
  console.log('🖼️ Gallery Assets Structure:');
  console.log('Total categories:', Object.keys(galleryAssets).length);
  
  // Warn if no assets found to help with debugging
  if (Object.keys(galleryAssets).length === 0) {
    console.warn('⚠️ No gallery assets loaded! Check:');
    console.warn('1. Folder structure: src/assets/prodgallery/{category}/{product}/');
    console.warn('2. File path in require.context() matches your structure');
    console.warn('3. Files have supported extensions (.png, .jpg, .mp4, etc.)');
    return galleryAssets;
  }
  
  Object.entries(galleryAssets).forEach(([category, products]) => {
    console.log(`\n📁 ${category.toUpperCase()}`);
    Object.entries(products).forEach(([productName, assets]) => {
      console.log(`  └─ ${productName}:`);
      console.log(`     Images: ${assets.images.length}, Videos: ${assets.videos.length}`);
      assets.all.forEach(asset => {
        console.log(`     - ${asset.fileName} (${asset.type})`);
      });
    });
  });
  return galleryAssets;
}

// Export the raw organized structure for advanced use cases
export { galleryAssets };

// Auto-debug on load (remove in production)
if (process.env.NODE_ENV === 'development') {
  console.log('📸 Gallery system initialized');
  console.log('Categories found:', Object.keys(galleryAssets).length);
  if (Object.keys(galleryAssets).length === 0) {
    console.warn('⚠️ No gallery assets detected. Run debugGalleryStructure() for details.');
  }
}